import type {
  CashCollectionMoneyHealthResponse,
  PlaidAccountMapResponse,
  PlaidAccountsListResponse,
  PlaidExchangeResponse,
  PlaidItemsListResponse,
  PlaidLinkTokenResponse,
} from '@repo/shared-types';
import { apiTransport as router } from './transport';
import { getContext } from './core';

export interface PlaidAccountMapBody {
  plaidItemId: string;
  plaidAccountId: string;
  /** Set to `null` to unmap from the ledger */
  bankAccountId: string | null;
  /** Required for property manager / owner org-scoped Plaid items */
  operatingOrganizationId?: string;
}

export const bankingApi = {
  createLinkToken: async (operatingOrganizationId?: string): Promise<PlaidLinkTokenResponse> => {
    const context = getContext();
    const body =
      operatingOrganizationId?.trim() != null && operatingOrganizationId.trim() !== ''
        ? { operatingOrganizationId: operatingOrganizationId.trim() }
        : {};
    return router.post<PlaidLinkTokenResponse>('/banking/link-token', body, context);
  },

  listPlaidItems: async (operatingOrganizationId?: string): Promise<PlaidItemsListResponse> => {
    const context = getContext();
    const filters: Record<string, string> = {};
    if (operatingOrganizationId?.trim()) {
      filters.operatingOrganizationId = operatingOrganizationId.trim();
    }
    return router.get<PlaidItemsListResponse>('/banking/plaid-items', context, filters);
  },

  listPlaidAccounts: async (
    plaidItemRowId: string,
    operatingOrganizationId?: string,
  ): Promise<PlaidAccountsListResponse> => {
    const context = getContext();
    const filters: Record<string, string> = { plaidItemId: plaidItemRowId };
    if (operatingOrganizationId?.trim()) {
      filters.operatingOrganizationId = operatingOrganizationId.trim();
    }
    return router.get<PlaidAccountsListResponse>('/banking/plaid-accounts', context, filters);
  },

  mapPlaidAccount: async (body: PlaidAccountMapBody): Promise<PlaidAccountMapResponse> => {
    const context = getContext();
    return router.post<PlaidAccountMapResponse>('/banking/plaid-accounts/map', body, context);
  },

  exchangePublicToken: async (payload: {
    publicToken: string;
    accountId?: string;
    operatingOrganizationId?: string;
  }): Promise<PlaidExchangeResponse> => {
    const context = getContext();
    return router.post<PlaidExchangeResponse>('/banking/exchange', payload, context);
  },

  getCashCollectionMoneyHealth: async (
    operatingOrganizationId: string,
  ): Promise<CashCollectionMoneyHealthResponse> => {
    const context = getContext();
    const filters: Record<string, string> = { operatingOrganizationId: operatingOrganizationId.trim() };
    return router.get<CashCollectionMoneyHealthResponse>('/banking/cash-collection-health', context, filters);
  },

  getOperatingOrganizationBillingSettings: async (
    operatingOrganizationId: string,
  ): Promise<{ operatingOrganizationId: string; allocationCashApplicationOrder: unknown }> => {
    const context = getContext();
    const filters: Record<string, string> = { operatingOrganizationId: operatingOrganizationId.trim() };
    return router.get<{ operatingOrganizationId: string; allocationCashApplicationOrder: unknown }>(
      '/banking/operating-organization-billing',
      context,
      filters,
    );
  },

  patchOperatingOrganizationBillingSettings: async (payload: {
    operatingOrganizationId: string;
    allocationCashApplicationOrder: ('LateFee' | 'Addon' | 'Rent')[] | null;
  }): Promise<{ operatingOrganizationId: string; allocationCashApplicationOrder: unknown }> => {
    const context = getContext();
    return router.patch<{ operatingOrganizationId: string; allocationCashApplicationOrder: unknown }>(
      '/banking/operating-organization-billing',
      payload,
      context,
    );
  },
};
